import React from "react";
import { Tooltip } from "@mui/material";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { useEditCameraStore } from "./editCameraStore/editCameraStore";
import {
  CameraMotionState,
  useCameraMotionStore,
} from "./cameraMotionOverride";

export type MotionVendor = "Axis" | "Mobotix";

/**
 * Why the camera-side motion plumbing is moot: the camera's own Motion setting
 * says Always stream ("camera"), or it inherits a site that has motion off
 * ("site"). null = motion governs this camera (or we can't tell): leave the
 * tab alone.
 */
export type MotionOffScope = "camera" | "site";

/**
 * Everything on the Axis/Mobotix Settings tabs is camera-side motion plumbing,
 * and whether any of it is used is decided by the camera's EFFECTIVE motion
 * setting: since ENG-2242 a camera can override its site (`use_motion_override`
 * on general_info; null = inherit, true = use motion, false = always stream),
 * and general_info's read-only `use_motion` is the resolved value. The page
 * load and the Motion card record both in `useCameraMotionStore`.
 *
 * - Resolved value known: it decides. Off with an explicit `false` override is
 *   the camera's own choice ("camera"); off while inheriting is the site's.
 * - Not known (store not filled for this camera, or an older payload without
 *   the fields): fall back to the site's `use_motion`, as before. Only an
 *   explicit `false` counts as off: `siteInfoEditCamera` is `{}` until the
 *   site loads, and a payload without the field shouldn't read as off.
 *
 * The camera override can turn the tab ON while the site is off. The Axis
 * backend's add_recipient / add_action_configuration still gate on the SITE's
 * `customer.use_motion`, so until admin switches those to the camera's setting
 * such an add may be rejected (the API error shows as usual).
 *
 * On some integrations motion does not govern these cameras at all, so there
 * is nothing to say and nothing to disable, whatever either value says:
 *
 * - **Milestone XProtect.** `camera_type` is the camera's make, not the site's
 *   integration, so an Axis-make camera recorded by Milestone still gets the
 *   Axis Settings tab — but there frames come from Milestone, and the resolved
 *   motion value (the override, else the per-camera `milestone_motion`,
 *   `camera_model.py`) is about Milestone's motion events, not this camera's
 *   own rules.
 * - **SMTP integrations** (`SMTP_per_camera`, which Hikvision SMTP also reports,
 *   and `speco_smtp`). The emailed frame *is* the signal, so admin forces
 *   `use_motion` to false on these sites (`Customer.configure_motion`), and
 *   every one of them reads as "off". The Axis backend ignores the flag there:
 *   `add_recipient` / `add_action_configuration` build the SMTP recipient and
 *   the send-clip-by-email rule whenever `integration.is_smtp`. Those cameras
 *   are not "streaming continuously instead", and these buttons are how one
 *   gets pointed at us. (The Motion card is hidden on these sites too.)
 *
 * Exact names on purpose: `speco_smtp` is lower-case, so a `.includes("SMTP")`
 * test would miss it.
 */
export const SITE_MOTION_FIELD_DOES_NOT_GOVERN = [
  "milestone",
  "SMTP_per_camera",
  "speco_smtp",
];

/** Pure decision behind `useMotionOff`; exported for tests. */
export const motionOffScope = ({
  integrationTypeName,
  siteUseMotion,
  camera,
}: {
  integrationTypeName: string | null | undefined;
  siteUseMotion: unknown;
  camera: CameraMotionState | null | undefined;
}): MotionOffScope | null => {
  if (SITE_MOTION_FIELD_DOES_NOT_GOVERN.includes(integrationTypeName ?? "")) {
    return null;
  }
  if (camera && typeof camera.effective === "boolean") {
    if (camera.effective) return null;
    return camera.override === false ? "camera" : "site";
  }
  return siteUseMotion === false ? "site" : null;
};

/**
 * `cameraId` picks the camera's recorded motion state; without one (or before
 * the page load records it) this is the old site-only check.
 */
export const useMotionOff = (
  cameraId?: string | number | null
): MotionOffScope | null => {
  const site = useEditCameraStore((state) => state.siteInfoEditCamera);
  const camera = useCameraMotionStore((state) =>
    cameraId != null ? state.byCamera[String(cameraId)] : undefined
  );
  return motionOffScope({
    integrationTypeName: site?.integration_type_name,
    siteUseMotion: site?.use_motion,
    camera,
  });
};

// "Motion is off" on its own reads as if we've stopped watching the feed
// entirely, so say what is happening instead.
export const motionOffTooltip = (
  vendor: MotionVendor,
  scope: MotionOffScope = "site"
) =>
  scope === "camera"
    ? `${vendor} motion is off for this camera — it is set to Always stream, so it streams continuously instead. Set this camera's Motion to Use motion (General Info tab) to enable these rules.`
    : `${vendor} motion is off for this site — cameras are streaming continuously instead. Turn Use Motion on for the site to enable these rules.`;

export const MotionOffNotice = ({
  vendor,
  scope = "site",
}: {
  vendor: MotionVendor;
  scope?: MotionOffScope;
}) => (
  <div className="mx-4 mt-4 rounded border-l-4 border-actuate-orange bg-[#FFF4E5] px-4 py-3 font-mulish text-actuate-blue">
    {scope === "camera" ? (
      <>
        <span className="font-bold">
          {vendor} motion is off for this camera.
        </span>{" "}
        It is set to Always stream, so it streams continuously instead. Set this
        camera&apos;s Motion setting on the General Info tab to Use motion to
        enable these motion rules.
      </>
    ) : (
      <>
        <span className="font-bold">{vendor} motion is off for this site.</span>{" "}
        Cameras are streaming continuously instead. Turn Use Motion on for the
        site to enable these motion rules.
      </>
    )}
  </div>
);

/**
 * An "add" control for a camera-side motion rule. With motion off the rule
 * would do nothing (and the API rejects the add anyway), so the control is
 * disabled and explains why. Deletes are NOT gated: the API accepts them with
 * motion off, and removing our recipient/rule is exactly what an operator who
 * just turned motion off needs to do. A disabled MUI Button swallows pointer events,
 * hence the wrapper element the Tooltip can bind to.
 */
export const MotionRuleButton = ({
  vendor,
  motionOff,
  onClick,
  text,
  disabled = false,
}: {
  vendor: MotionVendor;
  /** A scope (or legacy `true` = site) disables the control. */
  motionOff: MotionOffScope | boolean | null;
  onClick: () => void;
  text: string;
  /** Disables the control for another reason (e.g. a job already running). */
  disabled?: boolean;
}) =>
  motionOff ? (
    <Tooltip
      title={motionOffTooltip(
        vendor,
        motionOff === "camera" ? "camera" : "site"
      )}
      placement="top"
    >
      <div>
        <OrangeTextButton onClick={undefined} text={text} disabled={true} />
      </div>
    </Tooltip>
  ) : (
    <OrangeTextButton
      onClick={disabled ? undefined : onClick}
      text={text}
      disabled={disabled}
    />
  );
